import {findFieldMetadata, getFieldParentDetail} from "../common/Metadata";
import {
    ajaxJSON,
    ajaxJSONWithoutWait,
    ajaxJSONWithTask,
    areEqual,
    fieldValue,
    isAnyEqualToFirst,
    isAnyTrue,
    isEmptyOrNull,
    isNotEmptyOrNull,
    isNullOrUndefined
} from "../common/NamaCommon";
import * as CommonFieldIds from '../constants/CommonFieldIds'
import * as NamaURLS from '../constants/NamaURLS'
import {SAVE_AND_COMMIT, SAVE_DRAFT} from '../constants/NamaURLS'
import * as FieldType from '../constants/FieldType'
import * as CommonConstants from '../constants/CommonConstants'
import {fetchMinSearchQueryLength, userCanEditField} from "../login/Security";
import {
    createGenericValue,
    createGenericValueForList,
    createGenericValueForObject,
    createGenericValueForType,
    createReferenceGenericValue,
    createSelectRowIndexValue,
    createWholeEntityValue,
    getValue
} from "./GenericValues";
import {currentUserEmployeeId, currentUserId} from "../login/LoginPresenter";
import {
    allowDisablingCodeFieldWithSecurity,
    calcFieldValueTriggeredTemplate,
    getActionForField,
    getCustomGUISettings,
    isBarcodeOn
} from "../login/ClientCache";
import {EditPresenter} from "../routing/EditPresenter";
import * as DataType from '../constants/DataTypes';
import * as util from "../common/GUIUtilities";
import {getIds} from "../common/HasIDUtil";
import {removeAllFromList, removeObjectFromList, subtractTwoLists} from "../common/CollectionsUtility";
import {NamaRouterInstance} from "../routing/NamaRouter";
import * as ViewMode from "../constants/ViewMode";
import {openUrlInNewWindow, printNext} from "../common/NaMaPrinting";
import {PopupListView} from "../views/PopupListView"
import {translateNaMaText} from "../common/Translator"
import * as errors from "../common/ErrorsNotification";
import {EditPlace} from "../routing/places";
import * as GUIActionResultType from '../constants/GUIActionResultType'
import * as GridAction from "../constants/GridAction";
import {listReferences} from "../widgets/ReferenceWidget";
import {calcImage} from "../common/SuggestionItemUtil";
import {stringify} from "../common/NamaJSON";
import {NamaGridWidget} from "../grid/NamaGridWidget";
import {startPopup} from "../views/edit/PopupQuickCreator";
import {ReportLauncherPresenter} from "../routing/ReportLauncherPresenter";
import * as ApplicableWhen from "../constants/ApplicableWhen";
import {QuestionListWidget} from "../widgets/QuestionListWidget";
import {csvLineToList} from "../views/charts/DashBoardResultSetUtil";
import * as AutoSaveBehavior from "../constants/AutoSaveBehavior";
import {fieldHasSuggestionProvider} from "../widgets/FieldWidgetCreator";
import {showTooltip} from "../common/TooltipUtil";
import {gridFocusCell} from "../grid/GridEventsController";

const BULK_OPERATION = "@##blkop&&**";

/**
 *
 * @param {EditPresenter} presenter
 * @param {string} fieldId
 * @param {number} lineNumber
 */

export function performPostAction(presenter, fieldId, fieldValue, rowId, grid) {
    if (isAnyEqualToFirst(fieldId, CommonFieldIds.BOOK, CommonFieldIds.TERM, CommonFieldIds.GROUP) && fieldValue !== null && fieldValue.extraData) {
        let templateId = fieldValue.extraData.template;
        if (templateId && presenter.shouldApplyBookTermGroupTemplate(fieldId)) {
            presenter.applyTemplate(templateId, fieldId);
            return;
        }
    }
    let templateByFieldId = calcFieldValueTriggeredTemplate(presenter.entityType, fieldId, fieldValue);
    if (templateByFieldId) {
        presenter.applyTemplate(templateByFieldId, fieldId);
        return;
    }
    if (CommonFieldIds.CODE === fieldId || CommonFieldIds.ALT_CODE === fieldId)
        presenter.codeChanged(fieldValue, fieldId);
    if (presenter.isViewOnlyMode())
        return;
    presenter.clearErrors(fieldId);
    let fieldMetaData = findFieldMetadata(fieldId, presenter.metaData);
    if (!fieldMetaData)
        return;
    fieldMetaData.fieldInformation = fieldMetaData.fieldInformation || {};
    if (fieldMetaData.fieldInformation.hasPostAction) {
        asyncPostAction(fieldId, fieldMetaData, presenter, rowId, grid);
    }
    else {
        afterValidationActions(fieldId, null, rowId, presenter);
    }
}

function requestCodeSuggestions(id, currentText, presenter) {
    if (presenter.parentWidget instanceof CriteriaEditorWidget)
        return;
    let suggestions = [];
    let minSearchQueryLength = fetchMinSearchQueryLength(presenter.entityType);
    if (currentText.length < minSearchQueryLength)
        return suggestions;
    //TODO: add extra criteria from menu
    listReferences(presenter.entityType, 0, 25, currentText, createSearchContextInfo(id, -1, presenter), null).then((list) => {
        for (let ref of list.list) {
            let tempPresenter = new EditPresenter();
            tempPresenter.record = ref;
            suggestions.add({
                reference: ref,
                replacementString: tempPresenter.getCode(),
                displayString: calcImage(ref.id, ref.entityType,
                    tempPresenter.getCode() + " (" + tempPresenter.getNameForTitles() + ")", ref)
            });
        }
    });
    return suggestions;
}

export async function suggest(presenter, id, contextRow, currentText, entityTypeForGenericReference) {
    let suggestionList = [];
    if (CommonFieldIds.CODE === id) {
        return requestCodeSuggestions(id, currentText, presenter);
    }
    let fieldMetaData = findFieldMetadata(id, presenter.metaData);
    let fieldInformation = info(fieldMetaData);
    if (!fieldInformation || !fieldHasSuggestionProvider(fieldMetaData, presenter.entityType))
        return suggestionList;
    let allContextFieldsIds = [];
    if (fieldInformation.suggestionProviderContextFields)
        allContextFieldsIds.push(...fieldInformation.suggestionProviderContextFields);
    let suggestionProviders = getCustomGUISettings().tooltipsAndReportsAndRevise.suggestionProviders[presenter.entityType + "." + id];
    if (fieldInformation.dynamicSearchContextFields)
        allContextFieldsIds.push(...fieldInformation.dynamicSearchContextFields);
    if (suggestionProviders) {
        for (let suggestionProvider in suggestionProviders)
            allContextFieldsIds.push(...suggestionProvider.contextFields);
    }

    let contextFields = {};
    addContextFieldsToMap(id, allContextFieldsIds, contextRow, contextFields, FromGridAction.No, null, null, presenter);
    /**
     * @type {namaTypes.PerformPostActionParameters}
     */
    let postActionParameters = {};
    postActionParameters.entityType = presenter.entityType;
    postActionParameters.contextFields = contextFields;
    postActionParameters.barCodeOn = isBarcodeOn();
    postActionParameters.userId = currentUserId();
    postActionParameters.objectIndex = calcReqObjectIndex(contextRow, id, presenter);
    postActionParameters.parentId = id;
    return ajaxJSON(NamaURLS.PERFORM_Suggestion, {
        request: stringify(postActionParameters),
        currentText: currentText, entityTypeForGenericReference: entityTypeForGenericReference,
        fieldId: id, placeExtraFilter: presenter.getPlaceExtraFieldFilterId ? presenter.getPlaceExtraFieldFilterId() : null
    });

}

function extractPreValidationContextFieldIds(fields, fieldsWithPrevalidation, contextFieldsIds, presenter) {
    for (let field of fields) {
        let id = field.id;
        let fieldMetaData = findFieldMetadata(id, presenter.metaData);
        let fieldInformation = info(fieldMetaData);
        if (fieldInformation && fieldInformation.hasPreValidation) {
            fieldsWithPrevalidation.push(id);
            if (!contextFieldsIds.contains(id))
                contextFieldsIds.push(id);
            addListIfNotFound(contextFieldsIds, fieldInformation.preValidationContextFields);
        }
    }
}

function getFieldsContextsMap(fields, fieldWithPreValidation, contextFields, objectIndex, presenter) {
    let contextFieldsIds = [];
    extractPreValidationContextFieldIds(fields, fieldWithPreValidation, contextFieldsIds, presenter);
    addContextFieldsToMap(BULK_OPERATION, contextFieldsIds, objectIndex, contextFields, FromGridAction.No, [], {}, presenter);

}

/**
 * @param {EditPresenter} presenter
 */
function preValidate(presenter, allWidgets) {
    let fieldWithPreValidation = [];
    if (presenter.isViewOnlyMode()) {
        disableFields(presenter, allWidgets);
        return;
    }
    let all = [...allWidgets];

    for (let widget of allWidgets) {
        if (widget.isGridWidget )
            all.push(...widget.getColumns());
    }
    let securityDisabledFields = [];
    let approvalAllowEditingFields = calcApprovalAllowEditingFields(presenter);
    for (let i = all.length - 1; i >= 0; i--) {
        let current = all[i];
        if (!userCanEditField(presenter.entityType, current.id, presenter.calcFieldAuthorityMode()) || fieldNotEditableByApproval(approvalAllowEditingFields, current.id)) {

            if (current.isField || current.isGridWidget || current.showInGridView || current.showInFullView)
                securityDisabledFields.push(current);
            removeObjectFromList(all, current)
        }
    }
    disableFields(presenter, securityDisabledFields);

    let contextFields = {};
    getFieldsContextsMap(all, fieldWithPreValidation, contextFields, -1, presenter);
    /**
     * @type {namaTypes.BulkPrevalidationParameters}
     */
    let preValidationParameters = {};
    preValidationParameters.entityType = presenter.entityType;
    preValidationParameters.userId = currentUserId();
    preValidationParameters.contextFields = contextFields;
    preValidationParameters.barCodeOn = isBarcodeOn();
    preValidationParameters.fieldsToPrevalidate = fieldWithPreValidation;
    let fieldsToEnable = subtractTwoLists(getIds(allWidgets), fieldWithPreValidation);
    removeAllFromList(fieldsToEnable, getIds(securityDisabledFields));
    let fieldsToDisable = allWidgets.filter(w => {
        let fieldMetadata = findFieldMetadata(w.id, presenter.metaData);
        return fieldMetadata && (!fieldMetadata.enabled || fieldMetadata.systemField || !userCanEditField(presenter.entityType, fieldMetadata.fieldId, ApplicableWhen.All));
    });
    removeAllFromList(fieldsToEnable, fieldsToDisable);
    enableFields(fieldsToEnable, presenter);
    for (let widget of fieldsToDisable) {
        widget.setEnabled(false);
    }
    ajaxJSONWithTask(NamaURLS.PERFORM_PreValidation, stringify(preValidationParameters), (result) => {
        refreshWidgetState(result.states, -1, presenter);
    }, null, presenter, true);
}
/**
 * @param {EditPresenter} presenter
 */
function calcApprovalAllowEditingFields(presenter) {
    if (presenter.isApprovalPending())
        return csvLineToList(",", presenter.getFieldValue("currentApprovalCase.allowEditingFields"), true, false);
    return [];
}

function fieldNotEditableByApproval(approvalAllowEditingFields, id) {
    if (isEmptyOrNull(approvalAllowEditingFields))
        return false;
    return !approvalAllowEditingFields.contains(id);
}

/**
 * @param {EditPresenter} presenter
 */
function enableFields(fieldsToEnable, presenter) {
    let list = [];
    for (let field of fieldsToEnable) {
        /**
         * @type {namaTypes.SinglePrevalidationResult}
         */
        let singlePreValidationResult = {};
        singlePreValidationResult.state = "Enabled";
        singlePreValidationResult.fieldId = field;
        list.push(singlePreValidationResult);
    }
    refreshWidgetState(list, -1, presenter);
}

/**
 * @param {EditPresenter} presenter
 */
export function performPreValidation(presenter, fields, pageAuthority) {
    if (pageAuthority === "Disabled") {
        disableFields(presenter, fields);
    }
    else {
        preValidate(presenter, fields);
    }
}


/**
 * @param {EditPresenter} presenter
 */
function disableFields(presenter, fields) {
    let list = [];
    for (let field of fields) {
        if (allowDisablingCodeFieldWithSecurity() || (field && field.getId && field.getId() !== CommonFieldIds.CODE)) {
            /**
             * @type {namaTypes.SinglePrevalidationResult}
             */
            let singlePrevalidationResult = {};
            singlePrevalidationResult.state = "Disabled";
            singlePrevalidationResult.fieldId = field.id;
            list.push(singlePrevalidationResult);
        }
    }
    refreshWidgetState(list, -1, presenter);
}

/**
 *
 * @param {NamaGridWidget} grid
 * @param {Integer} row
 * @param {String} action
 * @param {EditPresenter} presenter
 * @param {ExtendedFieldMetaData} fieldMetadata
 * @param {Object} removed
 */
export function asyncGridAction(grid, row, action, presenter, fieldMetadata, removed) {
    if (grid.questionList)
        return;
    let fieldInfo = info(fieldMetadata);
    let allContextFieldsIds = [];
    let gridActionRequired = isGridActionRequired(action, fieldInfo);
    if (gridActionRequired) {
        addListIfNotFound(allContextFieldsIds, fieldInfo.gridActionContextFields);
    }
    let fieldsToPrevalidate = [];
    if (action !== GridAction.DeleteLine) {
        for (let column of grid.getColumns()) {
            let columnMetadata = findFieldMetadata(column.id, presenter.metaData);
            if (columnMetadata && columnMetadata.fieldInformation.hasPreValidation) {
                if (userCanEditField(presenter.entityType, column.id, presenter.calcFieldAuthorityMode())) {
                    addListIfNotFound(allContextFieldsIds, [column.id]);
                    addListIfNotFound(fieldsToPrevalidate, [column.id]);
                    addListIfNotFound(allContextFieldsIds, columnMetadata.fieldInformation.preValidationContextFields);
                }
            }
        }
    }
    if (!gridActionRequired && !fieldsToPrevalidate && action !== GridAction.InsertLine)
        return;
    if (gridActionRequired && action === GridAction.InsertLine)
        fieldsToPrevalidate = [];
    let contextFields = {};
    let sums = {};
    let contextRow = row > 0 ? row - 1 : row;
    addContextFieldsToMap(grid.id, allContextFieldsIds, contextRow, contextFields, FromGridAction.Yes, fieldInfo.sumsFields, sums, presenter);
    if (action === GridAction.DeleteLine)
        contextFields[CommonConstants.CONTEXTFIELD_WHOLELINE + grid.id] = createGenericValue(grid.id, presenter.metaData, removed, contextRow);
    /**
     * @type {namaTypes.PerformGridActionParameters}
     */
    let gridPostActionParameters = {};
    gridPostActionParameters.entityType = presenter.entityType;
    gridPostActionParameters.userId = currentUserId();
    gridPostActionParameters.barCodeOn = isBarcodeOn();
    gridPostActionParameters.parentId = grid.id;
    gridPostActionParameters.contextFields = contextFields;
    gridPostActionParameters.objectIndex = row;
    gridPostActionParameters.sums = sums;
    gridPostActionParameters.fieldsToPrevalidate = fieldsToPrevalidate;
    ajaxJSONWithTask(NamaURLS.PERFORM_Grid_ACTION, {request: stringify(gridPostActionParameters), gridId: grid.id, gridAction: action}, (result) => {
        if (result.gridActionResult)
            updateRelatedFields(result.gridActionResult, row, presenter);
        if (action === GridAction.InsertLine && gridActionRequired)
            prevalidateAllFieldsOfGridAtLine(grid, row, presenter);
        else if (result && result.fieldStateList && result.fieldStateList.states)
            refreshWidgetState(result.fieldStateList.states, row, presenter);
    });
}

function prevalidateAllFieldsOfGridAtLine(grid, row, presenter) {
    let fieldsToPrevalidate = [];
    let allContextFieldsIds = [];
    for (let i = 0; i < grid.columnCount; i++) {
        let columnId = grid.getColumns()[i].id;
        let fieldMetadata = findFieldMetadata(columnId, presenter.metaData);
        if (!fieldMetadata)
            continue;
        if (!fieldMetadata.fieldInformation)
            continue;
        if (!fieldMetadata.fieldInformation.hasPreValidation)
            continue;
        fieldsToPrevalidate.push(columnId);
        if (!allContextFieldsIds.contains(columnId))
            allContextFieldsIds.push(columnId);
        for (let prevContextField of fieldMetadata.fieldInformation.preValidationContextFields) {
            if (!allContextFieldsIds.contains(prevContextField))
                allContextFieldsIds.push(prevContextField);
        }
    }
    if (!fieldsToPrevalidate)
        return;
    let contextFields = {};
    addContextFieldsToMap(grid.id, allContextFieldsIds, row, contextFields, FromGridAction.Yes, [], {}, presenter);
    /**
     * @type {namaTypes.PerformGridActionParameters}
     */
    let gridPostActionParameters = {};
    gridPostActionParameters.entityType = presenter.entityType;
    gridPostActionParameters.userId = currentUserId();
    gridPostActionParameters.barCodeOn = isBarcodeOn();
    gridPostActionParameters.parentId = grid.id;
    gridPostActionParameters.contextFields = contextFields;
    gridPostActionParameters.objectIndex = row;
    gridPostActionParameters.sums = {};
    gridPostActionParameters.fieldsToPrevalidate = fieldsToPrevalidate;
    ajaxJSONWithTask(NamaURLS.PERFORM_Grid_ACTION, {
        request: stringify(gridPostActionParameters),
        gridId: grid.id,
        gridAction: GridAction.PrevalidateColumns
    }, (result) => {
        if (result.gridActionResult)
            updateRelatedFields(result.gridActionResult, row, presenter);
    });
}

function isGridActionRequired(action, fieldInfo) {
    if (fieldInfo && fieldInfo.hasGridAction && fieldInfo.gridActions.contains(action))
        return true;
    return false;
}

/**
 *
 * @param {NamaGridWidget} grid
 * @param {boolean} copyCurrentLine
 * @param {EditPresenter} presenter
 * @param {Integer} rowIndex
 */
export function afterRowInserted(grid, copyCurrentLine, rowIndex) {
    let presenter = grid.owner;
    let fieldMetadata = findFieldMetadata(grid.id, presenter.metaData);
    if (copyCurrentLine && gridHasValidationField(grid, presenter))
        asyncPostAction(fieldMetadata.fieldInformation.gridValidationField,
            findFieldMetadata(fieldMetadata.fieldInformation.gridValidationField, presenter.metaData), presenter, rowIndex);
    else
        asyncGridAction(grid, rowIndex, GridAction.InsertLine, presenter, fieldMetadata)
}

function gridHasValidationField(grid, presenter) {
    let fieldMetadata = findFieldMetadata(grid.id, presenter.metaData);
    if (!fieldMetadata)
        return false;
    if (!fieldMetadata.fieldInformation)
        return false;
    let info = fieldMetadata.fieldInformation;
    if (!info.gridValidationField)
        return false;
    // if (grid.containsColumn(info.gridValidationField))
    //     return true;
    return true;
}

export function calcReqObjectIndex(rowId, fieldId, presenter) {
    if (isNullOrUndefined(rowId))
        return -1;
    let objectIndex = typeof rowId === "number" ? rowId : presenter.getRowIndexByRowId(fieldId, rowId);
    return objectIndex;
}

/**
 *
 * @param fieldId
 * @param {ExtendedFieldMetaData} fieldMetadata
 * @param {EditPresenter} presenter
 */
function asyncPostAction(fieldId, fieldMetadata, presenter, rowId, grid) {
    let fieldInfo = info(fieldMetadata);
    let allContextFieldsIds = collectPostActionContextFieldsIds(fieldInfo, presenter);
    let contextFields = {};
    let sums = {};
    addContextFieldsToMap(fieldId, allContextFieldsIds, rowId, contextFields, FromGridAction.No, fieldInfo.sumsFields, sums, presenter)
    let fieldGenericValue = getFieldGenericValue(fieldId, rowId, presenter, rowId > -1);
    let parentId = getFieldParentDetail(fieldId, presenter.metaData);
    /**
     * @type {namaTypes.PerformPostActionParameters}
     */
    let postActionParameters = {};
    postActionParameters.entityType = presenter.entityType;
    postActionParameters.contextFields = contextFields;
    postActionParameters.barCodeOn = isBarcodeOn();
    postActionParameters.userId = currentUserId();
    postActionParameters.fieldValue = fieldGenericValue;
    postActionParameters.objectIndex = calcReqObjectIndex(rowId, fieldId, presenter);
    postActionParameters.parentId = parentId;
    postActionParameters.sums = sums;
    postActionParameters.fieldsToPrevalidate = collectEditableFieldsToPreValidate(fieldInfo, presenter);
    ajaxJSONWithTask(NamaURLS.PERFORM_POST_ACTION, {request: stringify(postActionParameters)}, (result) => {
        updateRelatedFields(result.postActionResult, rowId, presenter);
        if (result && result.fieldStateList && result.fieldStateList.states)
            refreshWidgetState(result.fieldStateList.states, rowId, presenter);
        afterValidationActions(fieldId, result, rowId, presenter);
        if (grid) {
            grid.settingRef = false;
            let grid_ = grid._kendoGrid();
            if (grid_.current() && !grid.stopDefaultFocus)
                gridFocusCell(grid, grid_, grid_.current().index(), ":eq(" + rowId + ")", null, 1);
        }
    }, (failure) => {
        validationFinished(null, fieldId, presenter, rowId);
    });
}

export function validateField(field, extraValues, presenter) {
    if (extraValues && extraValues.length) {
        handleExtraValues(field, extraValues, presenter);
    }
    if (isAnyEqualToFirst(field.id, CommonFieldIds.BOOK, CommonFieldIds.TERM, CommonFieldIds.GROUP)
        && field.value != null) {
        let templateId = field.value.extraData.template;
        if (templateId && presenter.shouldApplyBookTermGroupTemplate(field.id)) {
            presenter.applyTemplate(templateId, field.id);
            return;
        }
    }
    let templateByFieldId = calcFieldValueTriggeredTemplate(presenter.entityType, field.id, field.value);
    if (templateByFieldId) {
        presenter.applyTemplate(templateByFieldId, field.id);
        return;
    }
    if (areEqual(CommonFieldIds.CODE, field.id) || areEqual(CommonFieldIds.ALT_CODE, field.id))
        presenter.codeChanged(field.value);

    if (presenter.codeSateLiteFieldIds() && presenter.codeSateLiteFieldIds().length && presenter.codeSateLiteFieldIds().contains(field.id)) {
        let code = presenter.getFieldValue(CommonFieldIds.CODE, -1);
        if (code)
            code = presenter.getFieldValue(CommonFieldIds.ALT_CODE, -1);
        if (code)
            presenter.codeChanged(code);
    }
    if (presenter.isViewOnlyMode())
        return;
    presenter.clearErrors(field.id);
    let fieldMetadata = findFieldMetadata(field.id, presenter.metaData);
    let fieldInfo = info(fieldMetadata);
    if (fieldInfo == null || !(fieldMetadata.fieldInformation.hasPostAction)) {
        afterValidationActions(field.id, null);
    } else {
        asyncPostAction(field.id, fieldMetadata, presenter);
    }
}

function handleExtraValues(field, extraValues, presenter) {
    for (let extraValue of extraValues) {
        presenter.setFieldValue(extraValue.getX(), extraValue.getY());
    }
}

/**
 *
 * @param {EditPresenter} presenter
 */
function collectEditableFieldsToPreValidate(fieldInfo, presenter) {
    let returnedList = [];
    for (let fieldId of fieldInfo.prevalidatFields) {
        if (userCanEditField(presenter.entityType, fieldId, presenter.calcFieldAuthorityMode())) {
            returnedList.push(fieldId);
        }
    }
    return returnedList;
}

/**
 *
 * @param {string} fieldId
 * @param {namaTypes.PostActionsResult} result
 */
function afterValidationActions(fieldId, result, rowId, presenter) {
    if (result) {
        callPostActionsAfterField(result.postActionResult, presenter);
    }
    if (result && result.postActionResult && result.postActionResult.performGUIAction) {
        presenter.callGUIAction(result.postActionResult.performGUIAction);
    }
    performCustomAction(result.postActionResult, fieldId, rowId, presenter);
}
/**
 *
 * @param actionPlaceHolder {namaTypes.GUIActionPlaceholder}
 * @param presenter {EditPresenter}
 */
export function runCustomGuiPostAction(actionPlaceHolder, presenter) {
    performCustomAction_(null, actionPlaceHolder.customization.performActionAsField, -1, presenter, actionPlaceHolder.customization.guiPostActionsId);
}

function performCustomAction(values, fieldId, rowId, presenter) {
    performCustomAction_(values, fieldId, rowId, presenter);
}

function performCustomAction_(values, fieldId, rowId, presenter, guiPostActionsId) {
    /**
     *
     * @type {namaTypes.CmnGUIPostAction}
     */
    let action = getActionForField(fieldId, presenter.entityType, guiPostActionsId);
    if (!action) {
        validationFinished(values, fieldId, presenter, rowId);
        return;
    }
    let contextFieldIds = action.contextFields;
    contextFieldIds.push(action.fieldID);
    for (let contextField of contextFieldIds) {
        let index = contextFieldIds.indexOf(contextField);
        if (index > -1 && (contextField === null || contextField === undefined))
            this.splice(index, 1);
    }
    let context = {};
    addContextFieldsToMap(fieldId, contextFieldIds, rowId, context, FromGridAction.No, [], {}, presenter);
    /**
     *
     * @type {namaTypes.CustomPostActionReq}
     */
    let reqData = {
        fieldId: fieldId,
        context: context,
        manualGuiPostActionId: guiPostActionsId,
        entityType: presenter.entityType,
        lineNumber: rowId
    };
    ajaxJSONWithTask(NamaURLS.PerformCustomPostAction, {request: JSON.stringify(reqData)}, (result) => {
        updateRelatedFields(result.data, rowId, presenter);
        callPostActionsAfterField(result.data, presenter);
        if (result.data.performGUIAction)
            presenter.callGUIAction(result.data.performGUIAction, presenter.metaData);
        validationFinished(result.data, fieldId, presenter, rowId);
    });
}

/**
 * @param rowId
 * @param {EditPresenter} presenter
 */
function refreshWidgetState(states, rowId, presenter) {
    let hasFocusField = null;
    for (let state of states) {
        let fields = presenter.getFieldsWithIdInCurrentPage(state.fieldId);
        if (fields && fields.length > 0) {
            for (let field of fields) {
                if (util.applyWidgetState(field, state.state) && field.hasFocus)
                    hasFocusField = field;
            }
        }
        else {
            let columnGrids = presenter.getColumnGrids(state.fieldId);
            if (columnGrids && columnGrids.length > 0) {
                for (let naMaGrid of columnGrids) {
                    if (state.columnsStates) {
                        for (let i = 0; i < state.columnsStates.length; i++) {
                            naMaGrid.updatePrevalidationState({fieldId: state.fieldId, state: state.columnsStates[i]}, i);
                        }
                    }
                    else {
                        naMaGrid.updatePrevalidationState(state, rowId);
                    }
                }
            }
            else {
                let grid = presenter.getGrid(state.fieldId);
                if (grid)
                    util.applyWidgetState(grid, state.state);
            }
        }
    }
    if (hasFocusField != null)
        focusNext(hasFocusField, false, false, presenter);
}

/**
 *
 * @param {EditPresenter} presenter
 */
function focusNext(focusable, reverse, selectAllText, presenter) {
    //TODO:
}

export function updateValueInWidget(presenter, fieldId, fieldValue, i) {
    let widgetId = fieldId;
    let widgetsById = presenter.getFieldsWithId(widgetId);
    if (widgetsById && widgetsById.length) {
        for (let widget of widgetsById) {
            widget.setValue(fieldValue, fieldId, i);
            widget.removeErrorMarkerAndTooltip();
        }
    }
    else {
        let columnGrids = presenter.getColumnGrids(fieldId);
        if (columnGrids && columnGrids.length) {
            for (let grid of columnGrids) {
                if (grid.isGenReferenceEntityTypeCol(fieldId)) {
                    let entityType = fieldValue;
                    fieldValue = {entityType: entityType};
                    fieldId = fieldId.substring(0, fieldId.length - ".entityType".length);
                }
                grid.setValue(fieldValue, fieldId, i);
                grid.removeErrorMarkerAndTooltip(i, fieldId);
            }
        }
    }
}

/**
 *
 * @param {namaTypes.ListOfGenericValues} result
 * @param rowId
 * @param {EditPresenter} presenter
 */
export function updateRelatedFields(result, rowId, presenter) {
    if (result && result.pendingResponseId)
        checkForPendingResponseId(result.pendingResponseId, presenter, rowId, null);
    if (!result || !result.details)
        return false;
    let hasUpdates = false;
    let slaveFieldsUpdates = [];
    for (let fieldGenericValue of result.details) {
        if (fieldGenericValue.fieldId.startsWith(CommonConstants.ALLOWED_VALUES_PREFIX)) {
            let id = fieldGenericValue.fieldId.substring(CommonConstants.ALLOWED_VALUES_PREFIX.length);
            presenter.setAllowedValues(id, getValue(fieldGenericValue));
            //TODO: allowed values not working
            let widgetsById = presenter.getFieldsWithId(id);
            for (let widget in widgetsById)
                widget.setValue(getValue(fieldGenericValue));
            continue;
        }
        else if (fieldGenericValue.fieldId.startsWith(CommonFieldIds.SLAVE_ROWS)) {
            let parts = fieldGenericValue.fieldId.split(CommonConstants.HOPEFULLY_UNIQUE_FIELD_SEP);
            let actualFieldId = parts[1];
            let masterRowId = parts[2];
            /**
             * @type {SlaveFieldsTriple}
             */
            let slaveFieldsUpdate = {};
            slaveFieldsUpdate.actualFieldId = actualFieldId;
            slaveFieldsUpdate.masterRowId = masterRowId;
            slaveFieldsUpdate.fieldGenericValue = fieldGenericValue;
            slaveFieldsUpdates.push(slaveFieldsUpdate);
            //TODO: makesure slave rows works correctlly
            continue;
        }
        else if (fieldGenericValue.dataType === DataType.NORMALLIST) {
            let columnValues = getValue(fieldGenericValue);
            for (let i = 0; i < columnValues.size(); i++) {
                let fieldValue = getValue(columnValues[i]);
                presenter.setFieldValue(fieldGenericValue.fieldId, fieldValue, i);
                updateValueInWidget(presenter, fieldGenericValue.fieldId, fieldValue, i);
            }
        }
        else {
            let fieldValue = getValue(fieldGenericValue);

            if (fieldGenericValue.fieldId.contains(".") && rowId !== -1) {
                let fieldMetaData = findFieldMetadata(fieldGenericValue.fieldId, presenter.metaData);
                if (fieldMetaData && !fieldMetaData.parentDetail)
                    rowId = -1;
            }
            presenter.setFieldValue(fieldGenericValue.fieldId, fieldValue, rowId);
            updateValueInWidget(presenter, fieldGenericValue.fieldId, fieldValue, rowId);
        }
        hasUpdates = true;
    }
    for (let triple of slaveFieldsUpdates) {
        let list = presenter.getFieldValue(triple.actualFieldId, -1);
        let hasSlaveUpdates = false;
        let lineIndex = -1;
        if (list) {
            for (let i = 0; i < list.length; i++) {
                if (triple.masterRowId === list[i].id) {
                    lineIndex = i;
                }
            }
        }
        if (lineIndex == -1)
            return;
        let nextLineIndex = lineIndex + 1;
        while (nextLineIndex < list.length) {
            if (triple.masterRowId === list[nextLineIndex].masterRowId) {
                list.splice(nextLineIndex, 1);
                hasSlaveUpdates = true;
            } else {
                nextLineIndex++;
            }
        }
        if (triple.fieldGenericValue.listData.list && triple.fieldGenericValue.listData.list.length) {
            if (lineIndex == list.length - 1)
                list.push(...triple.fieldGenericValue.realFlatObject);
            else
                list.splice(lineIndex + 1, 0, ...triple.fieldGenericValue.realFlatObject);
            hasSlaveUpdates = true;
        }
        if (hasSlaveUpdates)
            presenter.bindDataWidgets();
        hasUpdates = hasUpdates || hasSlaveUpdates;
    }
    return hasUpdates;
}

/***
 *
 * @param {namaTypes.ListOfGenericValues}
 * @param fieldId
 * @param {EditPresenter} presenter
 */
function validationFinished(values, fieldId, presenter, rowId) {
    autoSaveAndFieldFocus(values, fieldId, presenter);
    showTooltip(presenter.entityType, fieldId, rowId, presenter, "", true);
    if (presenter.onFieldChanged)
        presenter.onFieldChanged(fieldId);
}

/**
 *
 * @param {namaTypes.ListOfGenericValues} values
 * @param fieldId
 * @param presenter
 */
function autoSaveAndFieldFocus(values, fieldId, presenter) {
    if (forceAutoSave(values)) {
        doAutoSave(presenter, values.focusFieldAfterAutoSave, values.addLineToGridAfterAutoSave, values.autoSaveBehavior);
        return;
    }
    let bookTermAndGroup = [];
    bookTermAndGroup.push(presenter.getFieldValue("term"), presenter.getFieldValue("book"), presenter.getFieldValue("group"));
    for (let ref of bookTermAndGroup) {
        if (!ref || !ref.extraData)
            continue;
        let autoSaveAfterInputInFields = ref.extraData[CommonConstants.AutoSaveAfterInputInFields];
        let focusFieldAfterAutoSave = ref.extraData[CommonConstants.FocusFieldAfterAutoSave];
        let autoSaveBehaviour = ref.extraData[CommonConstants.AutoSaveBehavior];
        let addLineToGrid = ref.extraData[CommonConstants.AddLineToGridAfterAutoSave];
        if (isEmptyOrNull(autoSaveAfterInputInFields))
            continue;
        let fields = csvLineToList(",", autoSaveAfterInputInFields);
        if (!fields.contains(fieldId))
            continue;
        let saveBehaviour = AutoSaveBehavior.AlwaysSaveAsDraft;
        if (areEqual(autoSaveBehaviour, AutoSaveBehavior.AlwaysSaveAndCommit))
            saveBehaviour = AutoSaveBehavior.AlwaysSaveAndCommit;
        doAutoSave(focusFieldAfterAutoSave, addLineToGrid, saveBehaviour);
        break;
    }
}

function forceAutoSave(values) {
    if (!values)
        return false;
    return isAnyTrue(values.forceCallAutoSave);
}

function doAutoSave(presenter, focusFieldAfterAutoSave, addLineToGrid, autoSaveBehaviour) {
    let saveBehaviour = areEqual(autoSaveBehaviour, AutoSaveBehavior.AlwaysSaveAndCommit) ? SAVE_AND_COMMIT : SAVE_DRAFT;
    if (presenter.record.commitedBefore)
        saveBehaviour = SAVE_AND_COMMIT;
    presenter.autoSave(focusFieldAfterAutoSave, addLineToGrid, saveBehaviour);
}

/**
 *
 * @param {string} fieldId
 * @param {Integer} lineNumber
 * @param [namaTypes.FieldTooltipInfo] fieldTooltipsInfo
 * @param {EditPresenter} presenter
 * @returns {Map<any, any>}
 */
export function mapParameters(fieldId, lineNumber, fieldTooltipsInfo, presenter) {

    let fields = [];
    if (fieldTooltipsInfo.length)
        fieldTooltipsInfo.forEach(fieldTooltipInfo => fields.push(...collectFieldsFromParameterMapping(fieldTooltipInfo)));
    else if (fieldTooltipsInfo.p1)
        fields.push(...collectFieldsFromParameterMapping(fieldTooltipsInfo));
    let distinctFields = [...new Set(fields)];
    let contextFields = new Map();
    addContextFieldsToMap(fieldId, distinctFields, lineNumber, contextFields, FromGridAction.No, null, null, presenter);
    return contextFields;
}

/**
 *
 * @param {namaTypes.FieldTooltipInfo} fieldTooltipInfo
 * @returns {Array}
 */
export function collectFieldsFromParameterMapping(fieldTooltipInfo) {
    let fields = [];
    add(fieldTooltipInfo.p1, fields);
    add(fieldTooltipInfo.p2, fields);
    add(fieldTooltipInfo.p3, fields);
    add(fieldTooltipInfo.p4, fields);
    add(fieldTooltipInfo.p5, fields);
    add(fieldTooltipInfo.p6, fields);
    add(fieldTooltipInfo.p7, fields);
    add(fieldTooltipInfo.p8, fields);
    add(fieldTooltipInfo.p9, fields);
    return fields;
}

/**
 *
 * @param {namaTypes.PMap} p
 * @param fields
 */
function add(p, fields) {
    if (p && p.field)
        fields.push(p.field);
}

/**
 *
 * @param {string} requester
 * @param {string[]} allContextFieldsIds
 * @param {string} rowId
 * @param {Object} contextFields
 * @param {string} fromGridAction
 * @param {string[]} allSumsIds
 * @param {Object} sums
 * @param {EditPresenter} presenter
 */
export function addContextFieldsToMap(requester, allContextFieldsIds, row, contextFields,
                                      fromGridAction, allSumsIds, sums, presenter) {
    _addContextFieldsToMap(requester, allContextFieldsIds, row, contextFields, fromGridAction, presenter);
    let commonFIelds = [CommonFieldIds.LEGAL_ENTITY, CommonFieldIds.SECTOR, CommonFieldIds.DEPARTMENT,
        CommonFieldIds.BRANCH, CommonFieldIds.ANALYSISSET, CommonFieldIds.BOOK, CommonFieldIds.TERM, CommonFieldIds.GROUP,
        CommonFieldIds.VALUE_DATE, CommonFieldIds.FISCAL_YEAR, CommonFieldIds.FISCAL_PERIOD, CommonFieldIds.ID, CommonFieldIds.COMMITE_BEFORE];
    _addContextFieldsToMap(requester, commonFIelds, row, contextFields, fromGridAction, presenter);
    if (sums)
        _addSumFieldsToMap(requester, allSumsIds, row, sums, presenter);
}

/**
 *
 * @param {string} requester
 * @param {striing[]} allSumsIds
 * @param {*} row
 * @param {object} sumsFields
 * @param {EditPresenter} presenter
 * @private
 */
function _addSumFieldsToMap(requester, allSumsIds, row, sumsFields, presenter) {
    if (!allSumsIds || !allSumsIds.length)
        return;
    for (let fieldId of allSumsIds) {
        let fieldDetail = getFieldParentDetail(fieldId, presenter.metadata);
        let requesterTheSameDetail = isRequesterTheSameDetail(requester, fieldDetail, presenter);
        let fieldIsWholeGrid = fieldDetail === fieldId;
        if (!requesterTheSameDetail || fieldIsWholeGrid)
            row = -1;
        sumsFields[fieldId] = getColumnSum(fieldId, fieldDetail, row, presenter);
    }
}

/**
 *
 * @param {string} fieldId
 * @param {string} fieldDetail
 * @param {string|number} execludedRow
 * @param {EditPresenter} presenter
 * @return {namaTypes.GenericValue}
 */
function getColumnSum(fieldId, fieldDetail, execludedRow, presenter) {
    let detailLines = presenter.getFieldValue(fieldDetail);
    let total = 0;
    let trimmedId = fieldId.substring(fieldDetail.length + 1)
    if (detailLines && detailLines.length) {
        let detailSize = detailLines.length;
        for (let i = 0; i < detailSize; i++) {
            let detailLine = detailLines[i];
            if (!detailLine)
                continue;
            if (execludedRow === i || detailLine.id === execludedRow)
                continue;
            let fieldVal = fieldValue(detailLine, trimmedId);
            if (fieldVal) {
                let numericValue = parseFloat(fieldVal);
                if (isNaN(numericValue)) {
                    console.log("Error parsing next object as number")
                    console.log(fieldVal);
                    throw new Error("Could not parse value " + fieldVal + " as number")
                }
                total += numericValue;
            }
        }
    }
    return createGenericValueForType(fieldId, total, FieldType.Decimal);
}

/**
 *
 * @param {string} requester
 * @param {string[]} allContextFieldsIds
 * @param {string} row
 * @param {Object} contextFields
 * @param {string} fromGridAction
 * @param {EditPresenter} presenter
 * @private
 */
function _addContextFieldsToMap(requester, allContextFieldsIds, row, contextFields,
                                fromGridAction, presenter) {
    for (let fieldId of allContextFieldsIds) {
        let fieldDetail = getFieldParentDetail(fieldId.replace(CommonConstants.CONTEXTFIELD_FORCE_SINGLE_COLUMN, ""), presenter.metaData);
        let genericValue;
        let shouldAddEntireRow = _shouldAddEntireRow(fieldId);
        let shouldAddSelectedRow = _shouldAddSelectedRow(fieldId);
        let shouldAddAllColumnValues = _shouldAddAllColumnValues(requester, fieldId, fieldDetail, fromGridAction, presenter);
        if (shouldAddEntireRow) {
            genericValue = getFieldGenericValue(fieldId.replace(CommonConstants.CONTEXTFIELD_WHOLELINE, ""), row, presenter, true);
        }
        else if (shouldAddSelectedRow) {
            let actualColumnId = fieldId.replace(CommonConstants.CONTEXTFIELD_SELECTEDLINE, "");
            //TODO: grid might have a problem here
            let selectedRowIndex = presenter.getColumnGridSelectedRowIndex(actualColumnId);
            if (!selectedRowIndex)
                throw new Error("Found multiple grids for column " + actualColumnId + " on current page");
            genericValue = getFieldGenericValue(actualColumnId, selectedRowIndex, presenter);

            contextFields[CommonConstants.CONTEXTFIELD_SELECTEDLINE_INDEX] = createSelectRowIndexValue(selectedRowIndex);
        }
        else if (CommonConstants.CONTEXTFIELD_WHOLE_ENTITY.equalsIgnoreCase(fieldId)) {
            genericValue = createWholeEntityValue(fieldId, presenter.getObject(), presenter.entityType);
        }
        else if (CommonConstants.CONTEXTFIELD_$_THIS.equalsIgnoreCase(fieldId)) {
            let value = presenter.toReference();
            genericValue = createReferenceGenericValue(fieldId, value);
        }
        else if (CommonConstants.CONTEXTFIELD_VISIBLE_FIELDS.equalsIgnoreCase(fieldId)) {
            genericValue = createGenericValueForList(fieldId, presenter.getAllVisibleFields(), "java.lang.String");
        }
        else if (shouldAddAllColumnValues) {
            genericValue = getAllColumnValues(fieldId, fieldDetail, presenter);
        }
        else if (CommonConstants.CRT_EXP_CURRENT_EMP_ID.equalsIgnoreCase(fieldId)) {
            let value = currentUserEmployeeId();
            genericValue = createGenericValueForType(fieldId, value, FieldType.Text);
        }
        else if (CommonConstants.CRT_EXP_CURRENT_USER_ID.equalsIgnoreCase(fieldId)) {
            let value = currentUserId();
            genericValue = createGenericValueForType(fieldId, value, FieldType.Text);
        }
        else {
            let fieldMetadata = findFieldMetadata(fieldId, presenter.metaData);
            if (fieldMetadata && fieldMetadata.fieldType === FieldType.Detail) {
                genericValue = createGenericValueForList(fieldId, presenter.getFieldValue(fieldId), fieldMetadata.detailClassName);
            }
            else if (!fieldMetadata)//A value object
            {
                if (fieldId.endsWith(".entityType") || fieldId.endsWith(".id"))
                    genericValue = createGenericValueForType(fieldId, presenter.getFieldValue ? presenter.getFieldValue(fieldId, row) : "", FieldType.Text);
                else
                    genericValue = createGenericValueForObject(fieldId, presenter.getFieldValue ? presenter.getFieldValue(fieldId, row) : null, presenter.entityType);
            }
            else
            {
                genericValue = getFieldGenericValue(fieldId, row, presenter, fieldMetadata.parentDetail);
            }

        }
        contextFields[fieldId] = genericValue;
    }
}

/**
 *
 * @param fieldId1
 * @param fieldDetail
 * @param {EditPresenter} presenter
 * @return {namaTypes.GenericValue}
 */
function getAllColumnValues(fieldId1, fieldDetail, presenter) {
    let fieldId = fieldId1.replace(CommonConstants.CONTEXTFIELD_FORCE_SINGLE_COLUMN, "");

    let detailLines = presenter.getFieldValue(fieldDetail) || [];
    let columnValues = detailLines.map((l) => getFieldGenericValue(fieldId, detailLines.indexOf(l), presenter, true));
    return createGenericValueForList(fieldId1, columnValues, "com.namasoft.common.flatobjects.GenericValue");
}

/**
 *
 * @param fieldId
 * @param row
 * @param {EditPresenter} presenter
 */
function getFieldGenericValue(fieldId, row, presenter, isDetail) {
    if (!isDetail || isDetail === false)
        row = undefined;
    if (!presenter || !presenter.getFieldValue)
        return createGenericValueForObject(fieldId, null);
    let value = presenter.getFieldValue(fieldId, row);
    return createGenericValue(fieldId, presenter.metaData, value, row);
}

function _shouldAddEntireRow(fieldId) {
    return fieldId.startsWith(CommonConstants.CONTEXTFIELD_WHOLELINE);
}

function _shouldAddSelectedRow(fieldId) {
    return fieldId.startsWith(CommonConstants.CONTEXTFIELD_SELECTEDLINE);
}

function _shouldAddAllColumnValues(requester, fieldId, fieldDetail, fromGridAction, presenter) {
    let fieldDetailNotEmptyOrNull = fieldDetail ? true : false;
    let requesterBulkOperation = isRequesterBulkOperation(requester);
    let detailNotField = fieldDetail !== fieldId;
    let requesterTheSameDetail = getFieldParentDetail(fieldId, presenter.metaData) == requester || isRequesterTheSameDetail(requester, fieldDetail, presenter);
    let forceAllColumn = fieldId.startsWith(CommonConstants.CONTEXTFIELD_FORCE_SINGLE_COLUMN);
    let shouldAddAllColumnValues = fieldDetailNotEmptyOrNull
        && (requesterBulkOperation || (detailNotField && !requesterTheSameDetail) || forceAllColumn);
    let gridActionAndRequesterIsParent = fromGridAction === FromGridAction.Yes && fieldDetail === requester;
    return shouldAddAllColumnValues && !gridActionAndRequesterIsParent;
}

function isRequesterBulkOperation(requester) {
    return requester === BULK_OPERATION;
}

/**
 *
 * @param requester
 * @param fieldDetail
 * @param {EditPresenter} presenter
 */
function isRequesterTheSameDetail(requester, fieldDetail, presenter) {
    return getFieldParentDetail(requester, presenter.metaData) === fieldDetail;
}

export const FromGridAction = {
    Yes: "Yes"
    , No: "No"
};

/**
 *
 * @param {ExtendedFieldMetaData} fieldMetadata
 * @return {namaTypes.FieldInformation}
 */
export function info(fieldMetadata) {
    return fieldMetadata && fieldMetadata.fieldInformation ? fieldMetadata.fieldInformation : null;
}

/**
 *
 * @param{namaTypes.FieldInformation} fieldInfo
 * @param {EditPresenter} presenter
 * @return {Array}
 */
function collectPostActionContextFieldsIds(fieldInfo, presenter) {
    let returnedList = [];
    addListIfNotFound(returnedList, fieldInfo.postActionContextFields);
    addListIfNotFound(returnedList, fieldInfo.validationContextFields);
    addPrevalidatedFieldsAndTheirContext(fieldInfo, returnedList, presenter);
    return returnedList;
}

/**
 *
 * @param{namaTypes.FieldInformation} fieldInfo
 * @param returnedList
 * @param {EditPresenter} presenter
 */
function addPrevalidatedFieldsAndTheirContext(fieldInfo, returnedList, presenter) {
    for (let fieldId of fieldInfo.prevalidatFields) {
        if (userCanEditField(presenter.entityType, fieldId, presenter.calcFieldAuthorityMode())) {
            addIfNotFound(returnedList, fieldId);
            let fieldInformation = info(findFieldMetadata(fieldId, presenter.metaData));
            if (fieldInformation)
                addListIfNotFound(returnedList, fieldInformation.preValidationContextFields);
        }
    }
}

/**
 *
 * @param {Array} targetList
 * @param {Array} toAdd
 */
function addListIfNotFound(targetList, toAdd) {
    if (!toAdd || !toAdd.length)
        return;

    for (let object of toAdd) {
        addIfNotFound(targetList, object);
    }
}

/**
 *
 * @param {Array} targetList
 * @param object
 */
function addIfNotFound(targetList, object) {
    if (!targetList.contains(object))
        targetList.push(object);
}

function afterFieldPOSTActions() {
    //TODO
}

/**
 *
 * @param fieldId
 * @param lineNumber
 * @param {EditPresenter} presenter
 * @return {namaTypes.FieldActionContextInfo}
 */
export function createSearchContextInfo(fieldId, lineNumber, presenter) {
    if (presenter instanceof ReportLauncherPresenter)
        return null;
    if (presenter instanceof NamaGridWidget) {
        if (!lineNumber || lineNumber === -1)
            lineNumber = presenter._getRowIndex();
        presenter = presenter.owner;
        if (presenter instanceof QuestionListWidget)
            presenter = presenter.owner;
    }
    if (!presenter.metaData)
        return null;
    let fieldMetadata = findFieldMetadata(fieldId, presenter.metaData);
    let fieldInfo = info(fieldMetadata);
    let hasSearcherContext = fieldInfo && fieldInfo.hasSearchContext;
    /**
     *
     * @type {namaTypes.FieldActionContextInfo}
     */
    let searchContextInfo = {};
    searchContextInfo.souceLineLinumber = lineNumber;
    searchContextInfo.sourceFieldId = fieldId;
    searchContextInfo.sourceEntityType = presenter.entityType;
    searchContextInfo.contextFields = {};
    searchContextInfo.placeExtraFilter = presenter.getPlaceExtraFieldFilterId();
    searchContextInfo.sourcFieldParentId = getFieldParentDetail(fieldId, presenter.metaData);
    let searchContextFields = hasSearcherContext ? fieldInfo.searchContextFields : [];
    searchContextFields = [...searchContextFields]; //copy fields to add dynamic fields
    if (fieldInfo && fieldInfo.dynamicSearchContextFields && fieldInfo.dynamicSearchContextFields.length)
        searchContextFields.push(...fieldInfo.dynamicSearchContextFields);
    addContextFieldsToMap(fieldId, searchContextFields, lineNumber, searchContextInfo.contextFields, FromGridAction.No, [], {}, presenter);
    let fieldGenericValue = getFieldGenericValue(fieldId, lineNumber, presenter, lineNumber > -1);
    searchContextInfo.sourceFieldValue = fieldGenericValue;
    return searchContextInfo;
}

function newPlace(createdObject) {
    let entityType = createdObject.entityType;
    if (!entityType)
        return null;
    let editPlace = new EditPlace();
    editPlace.entityType = entityType;
    editPlace.view = ViewMode.NEW;
    editPlace.createdObject = createdObject;
    editPlace.questions = new Date().getTime();
    return editPlace;
}

function callPostActionsAfterField(postActionResult, presenter) {
    if (!postActionResult || !postActionResult.callPostActorsForFields)
        return;
    for (let id of postActionResult.callPostActorsForFields) {
        let fields = presenter.getFieldsWithId(id);
        let postActoredField = fields[0];
        if (postActoredField) {
            performPostAction(presenter, postActoredField.id, postActoredField.value);
        }
        else {
            // TODO: later; qasid
        }
    }
}

/**
 *
 * @param {namaTypes.ListOfGenericValues} updateList
 * @param contextRow
 * @param {EditPresenter} presenter
 * @param {ExtendedNamaMetadata} metaData
 */
export function onGuiActionUpdateList(updateList, contextRow, presenter, metaData) {
    updateRelatedFields(updateList, contextRow, presenter);
    if (updateList.callPostActorsForFields) {
        callPostActionsAfterField(updateList, presenter, metaData);
    }
    if(isNotEmptyOrNull(updateList.performGUIAction))
        presenter.callGUIAction(updateList.performGUIAction, metaData);
}

function getExternalURLS(result) {
    let urls = result.externalURL.split(CommonConstants.URLS_SEPERATOR);
    return urls;
}

function getURLS(result) {
    let urls = result.internalURL.split(CommonConstants.URLS_SEPERATOR);
    return urls;
}

/**
 *
 * @param {namaTypes.GUIActionResult} result
 */
/**
 *
 * @param {namaTypes.GUIActionResult} result
 * @param {EditPresenter} presenter
 * @param {any} contextRow
 * @param {ExtendedNamaMetadata} metaData
 */
export function processGUIActionResult(result, presenter, contextRow, metaData) {
    let urls = [];
    result = result || {};
    result.type = result.type || "";
    switch (result.type) {
        case GUIActionResultType.NewRecord:
            let editPlace = newPlace(result.realObject);
            NamaRouterInstance.goTo(editPlace);
            break;
        case GUIActionResultType.UpdateList:
            onGuiActionUpdateList(result.updateList, contextRow, presenter, metaData);
            break;
        case GUIActionResultType.ExternalURL:
            urls = getExternalURLS(result);
            for (let url of urls) {
                if (result.inBrowserPopUpWindow)
                    window.open(url, "NamaPopup", "status=no,location=no,menubar=no");
                else
                    openUrlInNewWindow(url);
            }
            break;
        case GUIActionResultType.InternalURL:
            urls = getURLS(result);
            if (result.inPopup)
                openInPopup(urls);
            else if (result.inBrowserPopUpWindow) {
                for (let url of urls) {
                    window.open(url, "NamaPopup", "status=no,location=no,menubar=no");
                }
            } else if (result.inNewWindow) {
                for (let url of urls) {
                    openUrlInNewWindow(url);
                }
            } else if (urls.length > 1)
                printNext(urls, 0);
            else if (result.internalURL.startsWith("#"))
                window.location.hash = result.internalURL
            else
                window.location.href = window.location.href.replace(window.location.hash, "").replace("#", "") + result.internalURL
            break;
        case GUIActionResultType.PopupListView:
            new PopupListView(presenter, result.listView).show();
            break;
        case GUIActionResultType.BarcodePrint:
            util.printBarcode(result.printBarCode);
            break;
        case GUIActionResultType.GoToPlace:
            if (presenter.beforeGoToPlace)
                presenter.beforeGoToPlace();
            NamaRouterInstance.kendoRouter.navigate(result.place);
            break;
        case GUIActionResultType.NewRecordInPopup:
            util.showQuickCreate(result.realObject,
                value => {
                }, "", result.tabId);
            break;

        case GUIActionResultType.TextToCopy:
            util.showTextToCopy(result.textToCopy);
            break;
    }
    if (result.type !== GUIActionResultType.UpdateList && result.updateList && !result.updateList.empty)
        onGuiActionUpdateList(result.updateList, contextRow, presenter, metaData);

    if (result.messages) {
        for (let msg of result.messages) {
            if (result.messageIsError === true)
                errors.showErrors(translateNaMaText(msg));
            else
                errors.showInfo("", translateNaMaText(msg));
        }
    }
    if (result.afterPerformBehavior) {
        switch (result.afterPerformBehavior) {
            case `Refresh`:
                presenter.onRefresh();
                break;
            default:
                break;
        }
    }
    if (result.pendingResponseId) {
        checkForPendingResponseId(result.pendingResponseId, presenter, contextRow);
    }
}

function checkForPendingResponseId(pendingResponseId, presenter, contextRow) {
    let handler = () => {
        ajaxJSONWithoutWait(NamaURLS.CHECK_FOR_PENDING_GUI_ACTION_RESULT, {
            pendingResponseId: pendingResponseId
        }, (response) => {
            if (response)
                processGUIActionResult(response, presenter, contextRow, null);
            else
                setTimeout(handler, 1500);
        });
    }
    setTimeout(handler, 1500);
}

function openInPopup(urls) {
    let url = urls[0];
    if (url.startsWith("#"))
        url = url.substring(1);//remove first hash
    let place = NamaRouterInstance.calcPlace(url);
    if (!(place instanceof EditPlace))
        return;
    startPopup(place);
}